import { motion } from "framer-motion";
import { Users, ArrowRight, Shield, Heart, Zap, Brain } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";

const teamMembers = [
  {
    id: "rebecca-francis",
    name: "Rebecca Francis",
    role: "Founder & CEO",
    type: "founder",
    icon: Heart,
    color: "#14b8a6",
    imageUrl: "https://evcdikzpnjjpotbkkshs.supabase.co/storage/v1/object/public/public-bucket/RebeccaFrancis.jpeg",
    shortBio: "Diagnosed with refractory catamenial status epilepticus epilepsy in 2019. Faced with a lack of tracking tools for seizures and cycles, she set out to build the solution herself.",
    fullBio: "Diagnosed with refractory catamenial status epilepticus epilepsy in 2019. Faced with a lack of tracking tools for seizures and cycles, she set out to build the solution herself. She began by meticulously tracking symptoms, treatments, and patterns, searching for better ways to manage and predict neurological health challenges. Rebecca recognised the critical need for patient-driven health platforms connecting lived experience to actionable insights and research outcomes.",
    keyCredentials: [
      "Award-winning innovator with over 10 years' entrepreneurial experience",
      "$42M USD valuation on CommuniKids (EQVista 409a, 2024)",
      "Alumni of CSIRO Innovate to Grow, TRW Investment Ready Program, and Impact10x Accelerator",
      "Backed by TIF Sponsorship (2025) and recognised by industry leaders globally"
    ],
    mission: "Rebecca's mission: to ensure no seizure, tremor, or symptom ever goes unseen again.",
    linkedIn: "https://www.linkedin.com/in/becfrancis/"
  },
  {
    id: "joseph-skewes",
    name: "Joseph Skewes",
    role: "Co-Founder & Head of Patient Ecosystem",
    type: "founder",
    icon: Brain,
    color: "#06B6D4",
    imageUrl: "https://evcdikzpnjjpotbkkshs.supabase.co/storage/v1/object/public/public-bucket/JosephSkewes.jpeg",
    shortBio: "Lived experience with MND. Diagnosed at 40. Saw how limited the treatment options were and went into action.",
    fullBio: "Lived experience with MND. Diagnosed at 40. Saw how limited the treatment options were and went into action. He started tracking therapies, supplements, and symptoms, searching for potential ways to slow progression. Joseph recognised the power of community data and the need for patient-driven health platforms that connect lived experience with research outcomes.",
    keyCredentials: [
      "15+ years in technology and product innovation across large private enterprises, public service, and startups",
      "Alumni of Venture Catalyst, ThincLab, Unboxd, eChallenge, and SAYES",
      "Driven by empathy, curiosity, and a refusal to wait for someone else to build what patients need"
    ],
    vision: "Joseph's vision: empower every patient to become an active participant in their health journey.",
    linkedIn: "https://www.linkedin.com/in/josephskewes/"
  },
  {
    id: "tiago-ferreira",
    name: "Tiago Ferreira",
    role: "Cybersecurity & Certified Lead",
    type: "advisor",
    icon: Shield,
    color: "#8B5CF6",
    imageUrl: "https://evcdikzpnjjpotbkkshs.supabase.co/storage/v1/object/public/public-bucket/tiago.JPEG",
    shortBio: "Background: 20+ years with AFP, INTERPOL. Founder: North Star Strategies. Global advisor to governments and private sectors on cybersecurity and smart infrastructure.",
    fullBio: "Tiago Ferreira brings 20+ years of experience with the Australian Federal Police (AFP) and INTERPOL to his role as Cybersecurity & Certified Lead. As the founder of North Star Strategies, he serves as a global advisor to governments and private sectors on cybersecurity and smart infrastructure. Tiago has spearheaded global 'Smart Cities' initiatives and believes that robust cybersecurity is essential for protecting the trust patients place in digital health platforms."
  },
  {
    id: "liz-keen",
    name: "Liz Keen",
    role: "Clinical Governance Advisor",
    type: "advisor",
    icon: Heart,
    color: "#14b8a6",
    imageUrl: "https://evcdikzpnjjpotbkkshs.supabase.co/storage/v1/object/public/public-bucket/lizKeen.jpg",
    shortBio: "LIZ KEEN, RN. Current: Head of Clinical Governance at InfoSys (AU). Previous: Director at Bupa (AU). Delivered Australia's first Clinical Governance Framework for Digital Health.",
    fullBio: "Liz Keen, RN is a global executive nurse leader currently serving as Head of Clinical Governance at InfoSys (AU). Previously, she was Director at Bupa (AU) and delivered Australia's first Clinical Governance Framework for Digital Health. Liz is passionate about safe, patient-centred care in the digital age and brings extensive expertise in clinical governance and digital health integration."
  },
  {
    id: "michael-visser",
    name: "Dr. Michael Visser",
    role: "Clinical Education Advisor",
    type: "advisor",
    icon: Brain,
    color: "#A855F7",
    imageUrl: "https://evcdikzpnjjpotbkkshs.supabase.co/storage/v1/object/public/public-bucket/michaelVisser.JPEG",
    shortBio: "Role: Associate Lecturer at UQ. Founder: The Project Love App. Award-winning educator, co-authored Australia's first wellbeing guideline for perioperative nurses.",
    fullBio: "Dr. Michael Visser serves as Clinical Education Advisor with his role as Associate Lecturer at UQ. As the founder of The Project Love App, he is an award-winning educator who co-authored Australia's first wellbeing guideline for perioperative nurses. Dr. Visser has led UN/WHO lobbying for end-of-life care and champions compassion in healthcare, ensuring every patient journey is met with dignity and understanding."
  },
  {
    id: "peyman-obeidy",
    name: "Dr. Peyman Obeidy",
    role: "Founding Biomedical Advisor",
    type: "advisor",
    icon: Brain,
    color: "#06B6D4",
    imageUrl: "https://evcdikzpnjjpotbkkshs.supabase.co/storage/v1/object/public/public-bucket/PeymanObeidy.jpg",
    shortBio: "Education: Sydney University & Harvard Graduate. Awards: REDI Fellowship Recipient. Co-author on various biomedical research papers published in leading medical journals.",
    fullBio: "Dr. Peyman Obeidy serves as Founding Biomedical Advisor with education from Sydney University & Harvard Graduate. As a REDI Fellowship Recipient, he is co-author on various biomedical research papers published in leading medical journals. Dr. Obeidy is driven by innovation that bridges cutting-edge science with meaningful patient outcomes, bringing expertise in biomedical engineering and AI from his background at Harvard Medical School and the University of Sydney."
  }
];

export function TeamSection() {
  return (
    <section id="team" className="bg-white dark:bg-black py-20 relative z-10">
      <div className="container mx-auto px-6">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-black dark:text-white">
            Meet the <span className="text-teal-600 dark:text-teal-400">Dream Team</span>
          </h2>
          <p className="text-xl text-black dark:text-white max-w-3xl mx-auto">
            World-class experts united by a shared vision to revolutionise healthcare through AI-powered triage and advocacy.
          </p>
        </motion.div>

        {/* Founders */}
        <motion.div 
          className="mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <h3 className="text-2xl font-bold mb-8 text-center text-black dark:text-white">
            <span className="text-teal-600 dark:text-teal-400">Founders</span>
          </h3>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {teamMembers.filter(member => member.type === "founder").map((member, index) => (
              <motion.div
                key={member.id}
                className="p-10 rounded-xl border border-[#14b8a6] hover:border-[#14b8a6] hover:bg-opacity-80 transition-all duration-300 group"
                style={{
                  background: member.color === "#14b8a6" 
                    ? 'linear-gradient(135deg, rgba(20, 184, 166, 0.40) 0%, rgba(6, 182, 212, 0.35) 100%)'
                    : member.color === "#06B6D4"
                    ? 'linear-gradient(135deg, rgba(6, 182, 212, 0.40) 0%, rgba(8, 145, 178, 0.35) 100%)'
                    : 'linear-gradient(135deg, rgba(168, 85, 247, 0.40) 0%, rgba(147, 51, 234, 0.35) 100%)',
                  backdropFilter: 'blur(12px)',
                  border: member.color === "#14b8a6" ? '3px solid rgba(20, 184, 166, 0.6)' :
                          member.color === "#06B6D4" ? '3px solid rgba(6, 182, 212, 0.6)' :
                          '3px solid rgba(168, 85, 247, 0.6)',
                  boxShadow: member.color === "#14b8a6" ? '0 12px 48px rgba(20, 184, 166, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.1)' :
                           member.color === "#06B6D4" ? '0 12px 48px rgba(6, 182, 212, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.1)' :
                           '0 12px 48px rgba(168, 85, 247, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8, delay: 0.3 + index * 0.1 }}
              >
                <div className="flex items-start space-x-6">
                  <div 
                    className="w-36 h-36 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden"
                    style={{ 
                      border: `3px solid ${member.color}`,
                      filter: 'drop-shadow(0 0 25px rgba(20, 184, 166, 0.5)) drop-shadow(0 0 40px rgba(168, 85, 247, 0.4))',
                      background: `radial-gradient(circle, ${member.color}30, transparent)`
                    }}
                  >
                    {member.imageUrl ? (
                      <img 
                        src={member.imageUrl} 
                        alt={member.name} 
                        className="w-full h-full object-cover rounded-full"
                        onError={(e) => {
                          // Fallback to icon if image fails to load
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const iconContainer = target.parentElement;
                          if (iconContainer) {
                            const icon = document.createElement('div');
                            icon.innerHTML = `<${member.icon.name} size={48} style={{ color: '${member.color}' }} />`;
                            iconContainer.appendChild(icon);
                          }
                        }}
                      />
                    ) : (
                      <member.icon size={48} style={{ color: member.color }} />
                    )}
                  </div>
                  <div className="flex-1">
                    <h4 className="text-xl font-bold mb-1 text-black dark:text-white" style={{ color: member.color }}>
                      {member.name}
                    </h4>
                    <p className="text-gray-800 dark:text-gray-200 font-medium mb-3">{member.role}</p>
                    <p className="text-gray-800 dark:text-gray-200 text-sm leading-relaxed mb-4">
                      {member.shortBio}
                    </p>
                    
                    {member.keyCredentials && (
                      <div className="mb-4">
                        <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Key Credentials:</p>
                        <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                          {member.keyCredentials.map((credential, idx) => (
                            <li key={idx} className="flex items-start">
                              <span className="text-teal-500 mr-2">•</span>
                              <span>{credential}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {(member.mission || member.vision) && (
                      <div className="p-3 rounded-lg" style={{
                        background: 'linear-gradient(135deg, rgba(20, 184, 166, 0.30), rgba(168, 85, 247, 0.25))',
                        border: '1px solid rgba(20, 184, 166, 0.2)',
                        backdropFilter: 'blur(4px)'
                      }}>
                        <p className="text-xs font-medium text-gray-700 dark:text-gray-300 italic">
                          {member.mission || member.vision}
                        </p>
                      </div>
                    )}
                    
                    {member.linkedIn && member.linkedIn !== "#" && (
                      <div className="mt-3">
                        <a 
                          href={member.linkedIn}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-sm font-medium text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 transition-colors"
                        >
                          LinkedIn Profile →
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Advisors */}
        <motion.div 
          className="mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <h3 className="text-2xl font-bold mb-8 text-center text-black dark:text-white">
            <span className="text-teal-600 dark:text-teal-400">Expert Advisory Board</span>
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {teamMembers.filter(member => member.type === "advisor").map((member, index) => (
              <motion.div
                key={member.id}
                className="p-6 rounded-xl text-center group text-black dark:text-white"
                style={{
                  background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.4) 0%, rgba(15, 23, 42, 0.5) 100%)',
                  backdropFilter: 'blur(8px)',
                  border: '2px solid',
                  borderImage: 'linear-gradient(135deg, rgba(20, 184, 166, 0.3), rgba(168, 85, 247, 0.3)) 1',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05)'
                }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8, delay: 0.5 + index * 0.1 }}
              >
                <div className="text-center">
                  <div 
                    className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-4 overflow-hidden"
                    style={{ 
                      border: `2px solid ${member.color}`,
                      filter: 'drop-shadow(0 0 20px rgba(20, 184, 166, 0.4)) drop-shadow(0 0 30px rgba(168, 85, 247, 0.3))',
                      background: `radial-gradient(circle, ${member.color}20, transparent)`
                    }}
                  >
                    {member.imageUrl ? (
                      <img 
                        src={member.imageUrl} 
                        alt={member.name} 
                        className="w-full h-full object-cover rounded-full"
                        onError={(e) => {
                          // Fallback to icon if image fails to load
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const iconContainer = target.parentElement;
                          if (iconContainer) {
                            const icon = document.createElement('div');
                            icon.innerHTML = `<${member.icon.name} size={32} style={{ color: '${member.color}' }} />`;
                            iconContainer.appendChild(icon);
                          }
                        }}
                      />
                    ) : (
                      <member.icon size={32} style={{ color: member.color }} />
                    )}
                  </div>
                  <h4 className="text-lg font-bold mb-1 text-black dark:text-white" style={{ color: member.color }}>
                    {member.name}
                  </h4>
                  <p className="text-gray-800 dark:text-gray-200 font-medium mb-3 text-sm">{member.role}</p>
                  <p className="text-gray-800 dark:text-gray-200 text-xs leading-relaxed">
                    {member.shortBio}
                  </p>
                  
                  {member.keyCredentials && (
                    <div className="mt-3">
                      <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Credentials:</p>
                      <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                        {member.keyCredentials.slice(0, 2).map((credential, idx) => (
                          <li key={idx} className="flex items-start justify-center">
                            <span className="text-teal-500 mr-1">•</span>
                            <span>{credential}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA to full team page */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <Link href="/team">
            <Button
              className="bg-[#14b8a6] hover:bg-[#0f9488] text-black font-semibold px-8 py-3 flex items-center space-x-2 mx-auto transition-all rounded-lg"
            >
              <Users size={20} />
              <span>Meet the Full Team</span>
              <ArrowRight size={16} />
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}